import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  generalInfoSummaryQuery,
  scenarioLevelsQuery,
  getDecisionsInsights
} from './api';

import './styles.css';

// Hardcoded layout for the classified-multistep breakdown. Each row's
// `match` array mirrors the variants the backend may return for the
// `option` field (e.g. "Current Assets" vs "Current Assets Section"),
// matching the labels recognized by the gameplay component itself.
const BALANCE_SHEET_SECTIONS = [
  { label: 'Current Assets',         match: ['current assets', 'current assets section'] },
  { label: 'Non-Current Assets',     match: ['non-current assets', 'non-current assets section', 'long-term assets', 'long-term assets section'] },
  { label: 'Current Liabilities',    match: ['current liabilities', 'current liabilities section'] },
  { label: 'Non-Current Liabilities',match: ['non-current liabilities', 'non-current liabilities section', 'long-term liabilities', 'long-term liabilities section'] },
  { label: 'Equity',                 match: ['equity', 'equity section'] },
];

const INCOME_STATEMENT_SECTIONS = [
  { label: 'Gross Profits',     match: ['gross profit', 'gross profit section', 'gross profits', 'gross profits section'] },
  { label: 'Operating Profits', match: ['operating profit', 'operating profit section', 'operating profits', 'operating profits section', 'operating expenses', 'operating expenses section'] },
  { label: 'Non-Operating Items', match: ['non-operating', 'non-operating section', 'non-operating profits', 'non-operating profits section', 'non-operating items', 'other income/expenses', 'other income or expenses'] },
  // Realco-only Net Profit section (drop zone for Income Tax Expense).
  // Matches the option names the gameplay component recognizes.
  { label: 'Net Profit Section', match: ['net profit', 'net profit section', 'net income', 'net income section'] },
];

const MAX_PILLS_PER_SECTION = 6;
const norm = (s) => (s || '').toString().trim().toLowerCase();

// Drop a decision row into the first section whose `match` array
// contains its option string. Rows with no match (unknown sections,
// blanks) are ignored so the layout stays clean.
function bucket(rows, sections) {
  const buckets = sections.map(() => []);
  rows.forEach((row) => {
    const opt = norm(row.option);
    const idx = sections.findIndex(s => s.match.includes(opt));
    if (idx >= 0 && buckets[idx].length < MAX_PILLS_PER_SECTION) {
      buckets[idx].push(row);
    }
  });
  return buckets;
}

function SectionCard({ title, sections, buckets, variant }) {
  return (
    <div className={`multistep-breakdown-card${variant ? ` ${variant}` : ''}`}>
      <h2 className="multistep-breakdown-card-title">{title}</h2>
      <div className="multistep-breakdown-section-list">
        {sections.map((section, i) => (
          <div key={section.label} className="multistep-breakdown-section-row">
            <div className="multistep-breakdown-section-label">{section.label}</div>
            <div className="multistep-breakdown-section-cells">
              {buckets[i].map((row, j) => (
                <div
                  key={`${row.question}-${j}`}
                  className={`multistep-breakdown-pill ${row.is_correct ? 'is-correct' : 'is-incorrect'}`}
                >
                  {row.question}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClassificationSummaryDecisionBreakdownClassifiedMultistep() {
  const navigate = useNavigate();

  const {
    getUser,
    getCurrentClassificationGameId
  } = useAuth();

  const user = getUser();
  const gameId = getCurrentClassificationGameId();

  const { data: generalInfo } = useQuery(generalInfoSummaryQuery()) || {};
  const games = generalInfo?.games;
  const gamesSessions = generalInfo?.sessions;

  const game = games?.find(g => g.game_type.includes('Account Classification') && g.id === gameId);
  const scenarioId = game?.scenario;

  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({ scenarioId }));

  // Resolve the session whose decisions we display: the in-progress one
  // if it exists, otherwise the most recent completed session for this
  // game. Mirrors the same lookup used by the other summary pages.
  const currentGameSessionId = useMemo(() => {
    if (!gamesSessions || !gameId || !user?.id) return null;
    const own = gamesSessions.filter(s => s.user === user.id && s.game === gameId);
    const inProgress = own.find(s => !s.completed);
    if (inProgress) return inProgress.id;
    return own.filter(s => s.completed)
      .reduce((max, s) => (s.id > (max?.id || 0) ? s : max), null)?.id || null;
  }, [gamesSessions, gameId, user?.id]);

  const { data: decisionsInsights } = useQuery({
    queryKey: ['classificationDecisionsInsights', gameId, currentGameSessionId],
    queryFn: () => getDecisionsInsights({ gameId, gameSessionId: currentGameSessionId }),
    enabled: !!gameId && !!currentGameSessionId,
    refetchOnWindowFocus: false,
  });

  const isFirstGameOfTheType = useMemo(() => {
    if (!gamesSessions || !games) return false;
    return gamesSessions.filter(s =>
      games.find(g => g.id === s.game && g.game_type === 'Account Classification')
    ).length < 2;
  }, [gamesSessions, games]);

  // Flatten the insights response to one row per placement: {question,
  // option, is_correct}. The endpoint mirrors the consolidated-insights
  // shape — each row has a nested `options[]` describing how many
  // session-users picked each option. For a single session that means
  // count_users is 0 or 1, so the user's actual placement is the
  // option whose count_users > 0. Also accepts a pre-flattened shape
  // (row already carries `option` directly) so a future API change
  // doesn't silently break the page.
  //
  // If a row exposes level info we keep only classified-multistep
  // decisions; otherwise we trust the per-session endpoint to already
  // scope to the requested session and pass everything through.
  const multistepRows = useMemo(() => {
    if (!Array.isArray(decisionsInsights)) return [];
    // Both classified-multistep and realco (`real-public-co-*`) use the
    // same Classified Balance Sheet + Multi-Step Income Statement
    // gameplay, so the breakdown page treats them the same way.
    const isMultistepLevelName = (name) =>
      name === 'classified-multistep' || (name?.startsWith?.('real-public-co') ?? false);
    const multistepLevels = (scenarioLevels || []).filter(sl => isMultistepLevelName(sl.name));
    const multistepLevelNumbers = new Set(multistepLevels.map(sl => sl.level));
    const matchesMultistep = (row) => {
      // Endpoint exposes the numeric scenario level on each row
      // (e.g. level: 2). Match it against any of the scenario's
      // multistep-style level numbers so non-multistep level rows
      // (e.g. class-elements at level 1) get dropped.
      const levelName = row.scenario_level_name || row.level_name || row.level?.name;
      const levelNum = typeof row.level === 'number' ? row.level
        : (row.scenario_level || row.level_id || row.level?.id);
      if (levelName) return isMultistepLevelName(levelName);
      if (multistepLevelNumbers.size && levelNum != null) return multistepLevelNumbers.has(levelNum);
      return true;
    };

    const flat = [];
    decisionsInsights.forEach(row => {
      if (!matchesMultistep(row)) return;
      if (Array.isArray(row.options)) {
        const picked = row.options.find(o => (o.count_users || 0) > 0);
        if (picked) {
          flat.push({
            question: row.question,
            option: picked.option,
            is_correct: !!picked.is_correct,
          });
        }
      } else if (row.option) {
        flat.push({
          question: row.question,
          option: row.option,
          is_correct: !!row.is_correct,
        });
      }
    });
    return flat;
  }, [decisionsInsights, scenarioLevels]);

  const balanceSheetBuckets = useMemo(
    () => bucket(multistepRows, BALANCE_SHEET_SECTIONS),
    [multistepRows]
  );
  const incomeStatementBuckets = useMemo(
    () => bucket(multistepRows, INCOME_STATEMENT_SECTIONS),
    [multistepRows]
  );

  const handleNextClick = () => {
    navigate(isFirstGameOfTheType ? '/classification/summary/feedback' : '/classification/summary/final');
  };

  const handleBackClick = () => {
    navigate('/classification/summary/decision-breakdown');
  };

  return (
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      <div className="multistep-breakdown-container">
        <div className="multistep-breakdown-header">
          <h1 className="multistep-breakdown-page-title">Breakdown of Sorting Decisions</h1>
          <div className="multistep-breakdown-buttons">
            <button className="popup-messages-button button-navi no-margin" onClick={handleBackClick}>Back</button>
            <button className="popup-messages-button button-red no-margin" onClick={handleNextClick}>Next</button>
          </div>
        </div>
        <div className="multistep-breakdown-cards">
          <SectionCard
            title="Classified Balance Sheet"
            sections={BALANCE_SHEET_SECTIONS}
            buckets={balanceSheetBuckets}
          />
          <SectionCard
            title="Multi-Step Income Statement"
            sections={INCOME_STATEMENT_SECTIONS}
            buckets={incomeStatementBuckets}
            variant="income-statement"
          />
        </div>
      </div>
    </Layout>
  );
}
